import { colorBaseNeutralWhite } from '@duxton-web/design-tokens/themes/light';
import {
  FirstPageChevronUniversal,
  HideOutlined,
  LastPageChevronUniversal,
  SendUniversal,
  SettingsOutlined,
  ShowOutlined,
  SmartphoneUniversal,
} from '@duxton-web/icons';
import {
  Button,
  Divider,
  Dropdown,
  Flex,
  Row,
  Tabs,
  Typography,
} from '@duxton-web/ui';
import React, { useEffect, useMemo, useState } from 'react';

import aiLoading from 'img/ai_loading.gif';
import paperImage from 'img/paper.svg';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import SmartCampaignFormContentContainer from 'omega-rtc/components/form/ucd/SmartCampaignFormContentContainer';
import EmptyContent from 'omega-rtc/components/form/ucd/common/EmptyContent.tsx';
import LanguageSelectorModal from 'omega-rtc/components/form/ucd/common/SelectLanguageModal';
import supportedLanguages from 'omega-rtc/data/languages.json';

import SmartCampaignSendPreview from 'omega-rtc/components/form/ucd/SmartCampaignSendPreview';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';

interface SmartCampaignContentVariantsFormProps {
  isCollapsed: boolean;
  toggleSideBarCollapse: () => void;
}

const SmartCampaignContentVariantsForm: React.FC<SmartCampaignContentVariantsFormProps> = ({
  isCollapsed,
  toggleSideBarCollapse,
}) => {
  const {
    primaryLanguage,
    selectedLanguages,
    isCurrentContentSourceManual,
    handleLanguageSelect,
    numberOfSelectedVariants,
    isMystiqueCallPending,
    primaryLanguageContentsInForm,
  } = useCreateSmartCampaign();

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSelectedOnlyViewMode, setIsSelectedOnlyViewMode] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(primaryLanguage);
  const [isSelectLanguageModalOpen, setIsSelectLanguageModalOpen] =
    useState(false);

  /**
   * After user click “generate more”:
   * Need to switch to primary language tab(and need to show loading).
   */
  useEffect(() => {
    if (
      isMystiqueCallPending &&
      primaryLanguageContentsInForm.lastRoundNumber > 0
    ) {
      setActiveTabKey(primaryLanguage);
    }
  }, [isMystiqueCallPending]);

  useEffect(() => {
    // If the user removes currently selected tab. - then we change active tab to primary language.
    const isActiveTabExistInSelectedLanguage = selectedLanguages.find(
      (language) => language === activeTabKey,
    );

    if (!isActiveTabExistInSelectedLanguage) {
      setActiveTabKey(primaryLanguage);
    }
  }, [selectedLanguages]);

  const tabItems = useMemo(() => {
    return selectedLanguages.map((languageCode) => ({
      key: supportedLanguages[languageCode].code,
      label: supportedLanguages[languageCode].label,
      forceRender: true,
      children: (
        <SmartCampaignFormContentContainer
          language={languageCode}
          isPreviewMode={isPreviewMode}
          isSelectedOnlyViewMode={isSelectedOnlyViewMode}
        />
      ),
    }));
  }, [isPreviewMode, isCollapsed, selectedLanguages, isSelectedOnlyViewMode]);

  if (primaryLanguageContentsInForm.lastRoundNumber === 0) {
    const title = isCurrentContentSourceManual
      ? ''
      : 'Provide us with context, and we will suggest various content options for you';
    const description = isCurrentContentSourceManual
      ? 'Click next to start writing.'
      : 'Generated content variants will appear here';

    return (
      <Flex
        align="center"
        justify="center"
        style={{
          height: '100%',
          margin: 'auto',
          width: 370,
        }}
      >
        {isMystiqueCallPending ? (
          <img src={aiLoading} alt="loading..." width={60} height={60} />
        ) : (
          <EmptyContent
            image={paperImage}
            description={description}
            title={title}
          />
        )}
      </Flex>
    );
  }

  const menu = {
    items: [
      {
        key: 'preview',
        label: isPreviewMode ? 'Hide Image Preview' : 'Show Image Preview',
        icon: <SmartphoneUniversal />,
      },
      {
        key: 'only-selected',
        label: isSelectedOnlyViewMode
          ? 'Show All Variants'
          : 'Hide unselected Variant',
        icon: isSelectedOnlyViewMode ? <ShowOutlined /> : <HideOutlined />,
      },
      {
        key: 'send-preview',
        label: 'Send preview message',
        icon: <SendUniversal />,
      },
    ],
    onClick: ({ key }: { key: string }) => {
      switch (key) {
        case 'preview':
          setIsPreviewMode(!isPreviewMode);
          break;
        case 'only-selected':
          setIsSelectedOnlyViewMode(!isSelectedOnlyViewMode);
          break;
        case 'send-preview':
          if (numberOfSelectedVariants === 0) {
            snackBar.warning({
              content:
                'Please select content variants before sending preview message',
            });
            break;
          }
          setIsPreviewOpen(true);
          break;
        default:
          break;
      }
    },
  };

  return (
    <React.Fragment>
      <Row
        justify="space-between"
        align="center"
        style={{
          top: 0,
          zIndex: 5,
          position: 'sticky',
          padding: '16px 24px',
          backgroundColor: colorBaseNeutralWhite,
        }}
      >
        <Flex gap={8} align="center">
          <Button
            style={{ width: 28, height: 28 }}
            size="small"
            type="secondary"
            icon={
              isCollapsed ? (
                <LastPageChevronUniversal />
              ) : (
                <FirstPageChevronUniversal />
              )
            }
            onClick={toggleSideBarCollapse}
          />
          <Typography.Title style={{ margin: 0 }} level={4}>
            Content Variants
          </Typography.Title>
        </Flex>

        <Flex gap={8} align="center">
          <Dropdown menu={menu} trigger={['click']}>
            <Button size="small">Preview</Button>
          </Dropdown>
        </Flex>
      </Row>
      <Divider style={{ margin: 0 }} />

      <Tabs
        activeKey={activeTabKey}
        onTabClick={(activeKey) => setActiveTabKey(activeKey)}
        style={{ padding: '24px 40px', paddingBottom: 0, marginBottom: 24 }}
        type="pill"
        tabPosition="top"
        tabBarExtraContent={
          <Button
            ghost
            style={{ padding: 8, border: 0, height: 32 }}
            icon={<SettingsOutlined style={{ fontSize: 16 }} />}
            onClick={() => {
              setIsSelectLanguageModalOpen(true);
            }}
          />
        }
        items={tabItems}
      />

      <LanguageSelectorModal
        isManualMode={isCurrentContentSourceManual}
        isPrimaryLanguageSelectorDisabled={isMystiqueCallPending}
        isOpen={isSelectLanguageModalOpen}
        existingPrimaryLanguage={primaryLanguage}
        defaultSelectedLanguages={selectedLanguages}
        onSave={(languages) => {
          if (primaryLanguage !== languages[0]) {
            setActiveTabKey(languages[0]);
          }

          handleLanguageSelect(languages);
          setIsSelectLanguageModalOpen(false);
        }}
        onCancel={() => setIsSelectLanguageModalOpen(false)}
      />
      <SmartCampaignSendPreview
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        renderSelectedPrimaryLanguageVariants
        language={activeTabKey}
      />
    </React.Fragment>
  );
};

export default SmartCampaignContentVariantsForm;
